import { useNavigate, useLocation } from 'react-router-dom'
import { Logo } from './Logo'
import { CurrencyToggle } from './CurrencyToggle'
import { ThemeToggle } from './ThemeToggle'
import { useAuthStore } from '../../features/auth/store'
import { useTenantStore } from '../stores/useTenantStore'
import { useNavigationStore } from '../utils/navigationStore'
import { UserRole } from '../../features/auth/type'

const isCollectiveAdmin = (role: string | undefined) =>
  role === UserRole.COLLECTIVE_SUPERADMIN || role === UserRole.COLLECTIVE_ADMIN || role === UserRole.COLLECTIVE_USER;

interface HeaderProps {
  /** Called when hamburger menu is clicked - used for mobile sidebar toggle */
  onMenuToggle?: () => void
}

export const Header = ({ onMenuToggle }: HeaderProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const { activeMerchant, openSelector, clearTenant } = useTenantStore()
  const { sidebarCollapsed, toggleSidebarCollapsed } = useNavigationStore()
  const showTenantSwitcher = isCollectiveAdmin(user?.roleName)
  
  // Don't show currency toggle on create/edit product pages
  const shouldShowCurrencyToggle = !location.pathname.includes('/products/add') && 
                                   !location.pathname.includes('/products/edit')
  
  return (
    <header className="bg-card text-foreground border-b-[1px] border-border w-full h-[80px] lg:h-[128px] flex items-center px-4 lg:px-10 relative z-[60]">
      {/* Mobile hamburger menu */}
      <button 
        onClick={onMenuToggle}
        className="lg:hidden p-2 hover:bg-muted rounded-lg mr-2"
        aria-label="Toggle menu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Desktop sidebar collapse — same slot as the mobile hamburger, so the two never
          co-exist and the logo keeps its position at every breakpoint (PE-2240). */}
      <button
        onClick={toggleSidebarCollapsed}
        className="hidden lg:block p-2 hover:bg-muted rounded-lg mr-4 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!sidebarCollapsed}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M9 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path
            d={sidebarCollapsed ? 'M13 9l3 3-3 3' : 'M17 9l-3 3 3 3'}
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Logo - centered on mobile, left on desktop */}
      <div className="flex items-center lg:hidden flex-1 justify-center">
        <Logo className='w-[140px] h-[22px]' />
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:flex w-full justify-between items-center relative">
        <div className="flex items-center">
          <Logo className='w-[192px] h-[30px]' />
        </div>



        <div className="flex space-x-5 items-center">
          <ThemeToggle />
          {shouldShowCurrencyToggle && <CurrencyToggle />}

          {/* Merchant workspace switcher — collective admins only */}
          {showTenantSwitcher && (
            <div className="flex items-center gap-2">
              {activeMerchant ? (
                <button
                  onClick={openSelector}
                  className="flex items-center gap-2 pl-3 pr-2 py-1.5 border border-[#D9D9D9] rounded-[10px] hover:border-[#28282A] transition-colors group max-w-[200px]"
                >
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-[10px] text-muted-foreground leading-none">Viewing</span>
                    <span className="text-[13px] font-[600] text-foreground truncate max-w-[130px]">
                      {activeMerchant.name}
                    </span>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0 text-muted-foreground group-hover:text-foreground">
                    <path d="M7 15l5 5 5-5M7 9l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={openSelector}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#B8B5B6] rounded-[10px] hover:border-[#28282A] transition-colors text-[13px] text-muted-foreground hover:text-foreground"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9h18M3 15h18M12 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Select Merchant
                </button>
              )}
              {activeMerchant && (
                <button
                  onClick={() => clearTenant()}
                  title="Clear merchant context"
                  className="p-1.5 rounded-[8px] text-muted-foreground hover:text-[#E53E3E] hover:bg-red-50 transition-colors"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
          )}

          <button 
            onClick={() => navigate('/profile')}
            className="text-[19px] text-foreground font-[700] hover:bg-muted px-2 py-1 rounded"
          >
            Admin
          </button>
          {/* Two desktop notification bells used to sit here, both navigating to
              /notifications. No such route exists — the request fell through to the
              catch-all and silently bounced to the dashboard — and there is no
              notifications feature or backend at all behind it: notification-service is
              send-only with no Notification model (PE-2110 is blocked on exactly that).
              The affordance is removed rather than stubbed: a stub page would promise a
              feature that has nothing to show. Re-add ONE bell when the backend lands. */}
        </div>
      </div>

      {/* Mobile right icons */}
      <div className="flex lg:hidden items-center space-x-2">
        <ThemeToggle />
        {/* The mobile notification bell is removed for the same reason as the desktop
            pair above — /notifications is not a route and there is no backend behind it. */}
      </div>
    </header>
  )
}